import Decimal from 'decimal.js'

export interface TableSortQuery {
  sortBy?: string | undefined
  sortDesc?: boolean | undefined
}

export interface TableSortProps extends TableSortQuery {
  onSortChange?: (sortBy: string | undefined, sortDesc: boolean) => void
}

export type SortValue = string | number | null | undefined

export const sparklineChange = (points: readonly number[]): number =>
  (points.at(-1) ?? 0) - (points[0] ?? 0)

const BS_MONTHS = [
  'Baisakh',
  'Jestha',
  'Ashadh',
  'Shrawan',
  'Bhadra',
  'Ashwin',
  'Kartik',
  'Mangsir',
  'Poush',
  'Magh',
  'Falgun',
  'Chaitra',
]

export const bsDateSortValue = (value: string): number => {
  const [day, month, year] = value.split(' ')
  return Number(year) * 372 + BS_MONTHS.indexOf(month ?? '') * 31 + Number(day)
}

export const tradeTimeSortValue = (value: string): number => {
  const [clock, period] = value.split(' ')
  const [hours, minutes, seconds] = (clock ?? '').split(':').map(Number)
  return (
    (((hours ?? 0) % 12) + (period === 'PM' ? 12 : 0)) * 3600 + (minutes ?? 0) * 60 + (seconds ?? 0)
  )
}

/** Sort the complete filtered set before a service slices a numbered page. */
export const sortTableRows = <Row>(
  rows: readonly Row[],
  query: TableSortQuery,
  accessors: Partial<Record<string, (row: Row) => SortValue>>,
): Row[] => {
  const accessor = query.sortBy === undefined ? undefined : accessors[query.sortBy]
  if (accessor === undefined) return [...rows]

  const direction = query.sortDesc ? -1 : 1
  return rows
    .map((row, index) => ({ row, index }))
    .sort((left, right) => {
      const a = accessor(left.row)
      const b = accessor(right.row)
      if (a == null || b == null)
        return a == null && b == null ? left.index - right.index : a == null ? 1 : -1

      const numeric = !Number.isNaN(Number(a)) && !Number.isNaN(Number(b))
      const comparison = numeric
        ? new Decimal(a).cmp(new Decimal(b))
        : String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' })
      return comparison === 0 ? left.index - right.index : comparison * direction
    })
    .map(({ row }) => row)
}
